import i18next, { type i18n as I18n } from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import ja from "./ja.json";
import th from "./th.json";
import zhHans from "./zh-Hans.json";
import zhHant from "./zh-Hant.json";

/**
 * Staff UI languages ja (default) / th / en / zh-Hant / zh-Hans (design/05 §3; the two Chinese
 * ones since the owner's "หน้า POS ขอ Multilang", 2026-10-05, design/10 §7 #27). The language
 * follows the staff member signed in (their profile `locale` from identity-service), else the
 * tablet's last choice (Dexie `settings.locale`) — never localStorage (T07-22). Every screen has
 * the language picker (`components/LanguagePicker.tsx`). Text uses shop words only: no system
 * terms (checked by `tests/unit/i18n.test.ts`).
 */
export const POS_LOCALES = ["ja", "th", "en", "zh-Hant", "zh-Hans"] as const;
export type PosLocale = (typeof POS_LOCALES)[number];

/** Each language in its own words (the picker shows these, whatever the current language). */
export const POS_LOCALE_NAMES: Record<PosLocale, string> = {
  ja: "日本語",
  th: "ไทย",
  en: "English",
  "zh-Hant": "繁體中文",
  "zh-Hans": "简体中文",
};

export const resources = {
  en: { common: en },
  ja: { common: ja },
  th: { common: th },
  "zh-Hant": { common: zhHant },
  "zh-Hans": { common: zhHans },
} as const;

export function isPosLocale(value: unknown): value is PosLocale {
  return typeof value === "string" && (POS_LOCALES as readonly string[]).includes(value);
}

/** `ja-JP` / `th` / `en-US` / `zh-TW` / `zh-CN` … → one of the POS languages (ja when unknown). */
export function toPosLocale(value: string | null | undefined): PosLocale {
  const tag = (value ?? "").trim().toLowerCase().replace(/_/g, "-");
  if (tag === "zh" || tag.startsWith("zh-")) {
    return /-(hant|tw|hk|mo)(-|$)/.test(tag) ? "zh-Hant" : "zh-Hans";
  }
  const base = tag.split("-")[0];
  return isPosLocale(base) ? base : "ja";
}

export function createI18n(initialLanguage: PosLocale = "ja"): I18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: initialLanguage,
    fallbackLng: "ja",
    supportedLngs: [...POS_LOCALES],
    // "zh-Hant" is a resource name of its own — never fall back to a bare "zh"
    load: "currentOnly",
    defaultNS: "common",
    ns: ["common"],
    interpolation: { escapeValue: false },
    returnNull: false,
    initAsync: false,
  });
  return instance;
}

export const i18n = createI18n();
